import {
  AnimalCard,
  Card,
  ClinicalCarePriorityPicker,
  Flexbox,
  LoadManager,
  Messages,
  PageHeader,
  Styles,
  UserCard,
} from '@/components'
import {
  clinicalCareStatus,
  clinicalCareStatusClasses,
} from '@/constants/clinicalCares'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import useClinicalCare from '@/hooks/useClinicalCare'
import usePageTitle from '@/hooks/usePageTitle'
import useReadTimelineItem from '@/hooks/useReadTimelineItem'
import dateMask from '@/lib/masks/date'
import phoneNumber from '@/lib/masks/phoneNumber'
import { setClinicalCare } from '@/reducers/clinicalCare'
import clinicalCares from '@/services/api/clinicalCares'
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import formatAddress from '@/lib/formatters/address'
import { ANIMAL } from '@/lib/enums'
import Actions from './components/Actions'
import Events from './components/Events'
import Items from './components/Items'
import Prescritions from './components/Prescritions'
import QuestionnaireAnswers from './components/QuestionnaireAnswers'
import Records from './components/Records'
import StreetAnimalDetails from './components/StreetAnimalDetails'
import { ALLOWED_TO_CHANGE_PRIORITY } from './components/Actions/allowedActions'

export default function Show({ title }) {
  useReadTimelineItem()
  const { id } = useParams()
  usePageTitle({ title: `${title} #${id}` })
  const [changingPriority, setChangingPriority] = useState(false)
  const [updatingPriority, setUpdatingPriority] = useState(false)
  const {
    state: { clinicalCare },
    dispatch,
  } = useClinicalCare()

  const { data, loading, error } = useAxiosRequest(clinicalCares.show, id)

  const onChangePriority = useCallback(
    async (priority) => {
      if (priority.id !== clinicalCare?.priorityId) {
        try {
          setUpdatingPriority(true)
          const body = { priorityId: priority.id }
          const {
            data: updatedClinicalCare,
          } = await clinicalCares.changePriority(id, body)
          dispatch(setClinicalCare(updatedClinicalCare))
          toast.success('Prioridade alterada com sucesso')
        } catch (e) {
          toast.error(e.message)
        } finally {
          setUpdatingPriority(false)
          setChangingPriority(false)
        }
      }
    },
    [clinicalCare?.priorityId, dispatch, id],
  )

  useEffect(() => {
    if (data) dispatch(setClinicalCare(data))
  }, [data, dispatch])

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Atendimento clínico"
        icon="fas fa-stethoscope"
        subtitle={`Código: ${clinicalCare?.uid || '-'}`}
      />
      <LoadManager loading={loading || updatingPriority} error={error}>
        {Boolean(clinicalCare) && (
          <>
            <Actions clinicalCare={clinicalCare} />
            <div className="my-2">
              Situação:
              <b className={clinicalCareStatusClasses?.[clinicalCare.status]}>
                &nbsp;
                {clinicalCareStatus?.[clinicalCare.status]
                  || clinicalCare.status}
              </b>
            </div>
            <div className="my-2">
              Criado em: <b>{dateMask(clinicalCare.createdAt)}</b>
            </div>
            <div className="my-2">
              Agendado para: <b>{dateMask(clinicalCare.scheduledTo)}</b>
            </div>
            <div className="my-2">
              Protocolo: <b>{clinicalCare.protocolNumber || '-'}</b>
            </div>
            <div className="divider" />
            <div className="my-2">
              Nome da pessoa:{' '}
              <b>{clinicalCare?.extraData?.personName || '-'}</b>
            </div>
            <div className="my-2">
              Fone da pessoa:{' '}
              <b>
                {phoneNumber(clinicalCare?.extraData?.personPhoneNumber) || '-'}
              </b>
            </div>
            <div className="d-flex align-center">
              Prioridade: &nbsp;
              <span
                style={{
                  backgroundColor: clinicalCare.priority
                    ? clinicalCare.priority.color
                    : '#f4f4f4',
                  color: clinicalCare.priority ? '#fff' : 'inherit',
                  borderRadius: 5,
                  padding: '2px 5px',
                  fontWeight: 'bold',
                  fontSize: 12,
                }}
              >
                {clinicalCare.priority
                  ? clinicalCare.priority.title
                  : 'Não informada'}
              </span>
              {ALLOWED_TO_CHANGE_PRIORITY.includes(clinicalCare.status) && (
                <a
                  className="btn btn-sm btn-action btn-link tooltip tooltip-right"
                  data-tooltip="Alterar prioridade"
                  onClick={() => setChangingPriority(true)}
                >
                  <i className="fas fa-edit" />
                </a>
              )}
            </div>
            {Boolean(clinicalCare?.classification) && (
              <div className="d-flex align-center my-2">
                Classificação: &nbsp;
                <span
                  style={{
                    backgroundColor:
                      clinicalCare?.classification?.priority?.color
                      || '#f4f4f4',
                    color: clinicalCare?.classification?.priority
                      ? '#fff'
                      : 'inherit',
                    borderRadius: 5,
                    padding: '2px 5px',
                    fontWeight: 'bold',
                    fontSize: 12,
                  }}
                >
                  {clinicalCare?.classification?.description}
                </span>
                {clinicalCare?.classification?.priority && (
                  <span className="ml-1 text-small text-gray">
                    ({clinicalCare?.classification?.priority?.title})
                  </span>
                )}
              </div>
            )}

            <div className="mt-2 pt-2" />
            <h1 className="h5">Sintomas/Situação</h1>
            <div className="pre bg-secondary text-small p-2 s-rounded">
              {clinicalCare.description}
            </div>
            <div className="divider" />

            <QuestionnaireAnswers clinicalCare={clinicalCare} />
            {Boolean(
              clinicalCare?.clinicalCareId || clinicalCare?.nextClinicalCare,
            ) && <div className="divider" />}
            {Boolean(clinicalCare?.clinicalCareId) && (
              <div className="my-2">
                Atendimento anterior:
                <Link
                  to={`/app/clinical-cares/status/${clinicalCare?.clinicalCare?.status || 'all'
                    }/${clinicalCare.clinicalCareId}`}
                >
                  <b> #{clinicalCare.clinicalCareId}</b>
                  <small>
                    &nbsp;- Atendido em &nbsp;
                    {dateMask(clinicalCare?.clinicalCare?.hospitalizedAt)}
                  </small>
                </Link>
              </div>
            )}
            {Boolean(clinicalCare?.nextClinicalCare) && (
              <div className="my-2">
                Atendimento de retorno:
                <Link
                  to={`/app/clinical-cares/status/${clinicalCare?.nextClinicalCare?.status || 'all'
                    }/${clinicalCare?.nextClinicalCare?.id}`}
                >
                  <b>&nbsp;#{clinicalCare?.nextClinicalCare?.id}</b>
                  <small>
                    &nbsp;- Agendado para &nbsp;
                    {dateMask(clinicalCare?.nextClinicalCare?.scheduledTo)}
                  </small>
                </Link>
              </div>
            )}
            {Boolean(clinicalCare?.rejectionReason) && (
              <>
                <div className="my-2">
                  Motivo da rejeição:{' '}
                  <b>{clinicalCare?.rejectionReason?.description}</b>
                </div>
                <div className="divider" />
              </>
            )}
            {Boolean(clinicalCare?.partnerBlacklist) && (
              <>
                <div className="toast toast-error">
                  <div className="h6 text-bold">Este usuário foi bloqueado</div>
                  <div className="pre">
                    {clinicalCare.partnerBlacklist.description}
                  </div>
                  <div className="pre mt-2 text-small">
                    {clinicalCare.partnerBlacklist.info}
                  </div>
                  <div className="mt-2 text-right">
                    <Link
                      to={`/app/blacklist/${clinicalCare.partnerBlacklist.id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <i className="fas fa-eye" />
                      &nbsp;Exibir bloqueio
                    </Link>
                  </div>
                </div>
                <div className="divider" />
              </>
            )}
            <Flexbox.Columns className="mt-2">
              {clinicalCare.animal ? (
                <>
                  <Flexbox.Column className="col-6 col-sm-12 mb-2 pb-2">
                    <AnimalCard animal={clinicalCare.animal} />
                  </Flexbox.Column>
                  <Flexbox.Column className="col-6 col-sm-12 mb-2 pb-2">
                    <UserCard
                      title="Tutor / Responsável"
                      user={clinicalCare?.tutor}
                      lowIncome={clinicalCare?.lowIncome}
                      renderFooter={() => (
                        <Card.Footer>
                          {Boolean(clinicalCare?.tutor?.address) && (
                            <>
                              <div className="divider" />
                              {formatAddress(clinicalCare?.tutor?.address)}
                              <br />
                            </>
                          )}
                          {Boolean(
                            clinicalCare?.countClinicalCaresByTutor
                            !== undefined,
                          ) && (
                              <>
                                <div className="divider" />
                                <span className="chip">
                                  Atendimentos clínicos ativos:{' '}
                                  {clinicalCare?.countClinicalCaresByTutor}
                                </span>
                              </>
                            )}
                        </Card.Footer>
                      )}
                    />
                  </Flexbox.Column>
                </>
              ) : (
                <Flexbox.Column>
                  <Card.Base>
                    {
                    Boolean(clinicalCare?.animalData) && (
                      <Card.Header>
                        <Flexbox.Columns className="align-center">
                          <Flexbox.Column>
                            <div className="h5">Pet</div>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-auto">
                            <i className="fas fa-paw" />
                          </Flexbox.Column>
                        </Flexbox.Columns>
                      </Card.Header>
                    )
                  }
                    <Card.Body>
                      {
                        clinicalCare?.animalData ? (
                          <div>
                            <div className="text-bold">Animal de rua</div>
                            <div>
                              {clinicalCare?.animalData?.category?.description}
                              &nbsp;- {ANIMAL.gender.t(clinicalCare?.animalData?.gender)}
                            </div>
                            {
                              Boolean(clinicalCare?.animalData?.obs) && (
                                <>
                                  <div className="divider" />
                                  <div>clinicalCare?.animalData?.obs</div>
                                </>
                              )
                            }
                          </div>
                        )
                          : (
                            <div>O pet ainda não foi informado, informe antes de encaminhar
                              para a clínica
                            </div>
                          )
                      }
                    </Card.Body>
                  </Card.Base>
                </Flexbox.Column>
              )}
            </Flexbox.Columns>

            <div className="divider" />
            <div className="my-2 py-2">
              <StreetAnimalDetails clinicalCare={clinicalCare} />
            </div>

            <div className="divider" />
            <div className="my-2 py-2">
              <UserCard title="Fornecedor" user={clinicalCare?.provider} />
            </div>

            <h1 className="h5">Envolvidos</h1>
            <Flexbox.Columns>
              <Flexbox.Column className="col-6 col-sm-12 my-2">
                <UserCard title="Autor" user={clinicalCare.author} />
              </Flexbox.Column>
              {Boolean(clinicalCare?.clinic) && (
                <Flexbox.Column className="col-6 col-sm-12 my-2">
                  <UserCard title="Clínica" user={clinicalCare.clinic} />
                </Flexbox.Column>
              )}
            </Flexbox.Columns>
            <div className="divider" />
            <div className="my-2 py-2">
              <Records records={clinicalCare.records} />
            </div>

            <div className="divider" />
            <div className="my-2 py-2">
              <Items items={clinicalCare.items} price={clinicalCare.price} />
            </div>

            <div className="divider" />
            <div className="my-2 py-2">
              <Prescritions
                prescritions={clinicalCare.prescritions}
                clinicalCareUid={clinicalCare.uid}
              />
            </div>

            <div className="divider" />
            <div className="my-2 py-2">
              <h1 className="h5 mb-2">Eventos do atendimento</h1>
              <Events
                events={clinicalCare?.events}
                providerCity={clinicalCare?.provider?.city}
              />
            </div>
            <ClinicalCarePriorityPicker
              active={changingPriority}
              selected={clinicalCare.priority}
              onClose={() => setChangingPriority(false)}
              onSelect={onChangePriority}
            />
            <Messages
              addMessage={(body) => clinicalCares.addMessage(id, body)}
              clinicId={clinicalCare.clinicId}
              providerId={clinicalCare.providerId}
              fetchList={() => clinicalCares.messages(id)}
            />
          </>
        )}
      </LoadManager>
    </Styles.Container>
  )
}
